// src/pages/AdminPublicationsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Alert,
  CircularProgress,
  Link,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { 
  getPublications, 
  createPublication, 
  updatePublication, 
  deletePublication, 
  Publication 
} from '../api/publicationService';
import './AdminPublicationsPage.css';

const AdminPublicationsPage: React.FC = () => {
  const { user } = useAuth();
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [open, setOpen] = useState(false);
  const [currentPublication, setCurrentPublication] = useState<Partial<Publication>>({});
  const [isEditing, setIsEditing] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publicationToDelete, setPublicationToDelete] = useState<number | null>(null);

  // Fetch publications
  const fetchPublications = async () => {
    try {
      setLoading(true);
      const data = await getPublications();
      setPublications(data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching publications:', err);
      setError(`Failed to load publications: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublications();
  }, []);

  // Handle dialog open for create/edit
  const handleOpenDialog = (publication?: Publication) => {
    if (publication) {
      setCurrentPublication(publication);
      setIsEditing(true);
    } else {
      setCurrentPublication({
        title: '',
        year: new Date().getFullYear(),
        link: ''
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpen(false);
    setCurrentPublication({});
  };

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentPublication({
      ...currentPublication,
      [name]: name === 'year' ? parseInt(value, 10) : value
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      if (isEditing && currentPublication.id) {
        await updatePublication(currentPublication.id, currentPublication);
        setSuccess('Publication updated successfully');
      } else {
        await createPublication(currentPublication as Omit<Publication, 'id'>);
        setSuccess('Publication created successfully');
      }
      handleCloseDialog();
      fetchPublications();
    } catch (err: any) {
      setError(`Failed to save publication: ${err.message}`);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    setPublicationToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (publicationToDelete) {
      try {
        await deletePublication(publicationToDelete);
        setSuccess('Publication deleted successfully');
        fetchPublications();
      } catch (err: any) {
        setError(`Failed to delete publication: ${err.message}`);
      }
    }
    setDeleteDialogOpen(false);
    setPublicationToDelete(null);
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Check if user can edit (admin or editor)
  const canEdit = user && ['admin', 'editor'].includes(user.role);
  
  // Check if user can delete (admin only)
  const canDelete = user && user.role === 'admin';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} className="admin-publications">
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Publications
          </Typography>
          
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Publication
            </Button>
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} className="success-message">
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} className="error-message">
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Link</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {publications.map((pub) => (
                  <TableRow key={pub.id}>
                    <TableCell className="publication-title">
                      {pub.title}
                    </TableCell>
                    <TableCell>
                      <span className="publication-year">{pub.year}</span>
                    </TableCell>
                    <TableCell>
                      {pub.link && (
                        <Link 
                          href={pub.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="publication-link"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          View <OpenInNewIcon fontSize="small" />
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <IconButton onClick={() => handleOpenDialog(pub)} color="primary">
                          <EditIcon />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton onClick={() => handleDelete(pub.id!)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {publications.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No publications found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Publication' : 'Add Publication'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            label="Title"
            name="title"
            fullWidth
            value={currentPublication.title || ''}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="normal"
            label="Year"
            name="year"
            type="number"
            fullWidth
            value={currentPublication.year || new Date().getFullYear()}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="normal"
            label="Link"
            name="link"
            fullWidth
            value={currentPublication.link || ''}
            onChange={handleInputChange}
            helperText="URL to the publication"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this publication? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPublicationsPage;
